'use client';

import { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback } from 'react';
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';
import { Session } from '@supabase/supabase-js';

type AuthContextType = {
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshAuth: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  isLoading: true,
  isAuthenticated: false,
  refreshAuth: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Use the official auth helper client
  const supabase = createClientComponentClient(); 
  const lastRefreshTime = useRef<number>(0);
  const refreshInProgress = useRef<boolean>(false);
  const initializeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const authTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // More aggressive throttling to prevent refresh loops
  const THROTTLE_TIME = 3000; // 3 seconds between refreshes

  const refreshAuth = useCallback(async () => {
    // Throttle refreshes - don't allow more than one every THROTTLE_TIME
    const now = Date.now();
    if (now - lastRefreshTime.current < THROTTLE_TIME || refreshInProgress.current) {
      console.log("Refresh auth throttled or already in progress, skipping");
      return;
    }

    refreshInProgress.current = true;
    let isAuthenticatedAfterRefresh = false;
    
    try {
      // Only show loading if we don't already have a session
      if (!session) {
        setIsLoading(true);
      }
      
      console.log("Starting auth refresh...");
      lastRefreshTime.current = now;
      
      // Get fresh session from Supabase using auth-helpers client
      // which properly reads from cookies
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error("Error getting session:", error.message);
        setSession(null);
        setIsLoading(false);
        return;
      }
      
      const newSession = data.session;
      console.log("Auth refresh result:", newSession ? "Session found" : "No session");
      
      if (newSession && newSession.expires_at) {
        const expiresAt = new Date(newSession.expires_at * 1000);
        console.log("Session expires at:", expiresAt.toISOString());
        
        // Check if session is expired already
        if (expiresAt < new Date()) {
          console.log("Session already expired, clearing it");
          setSession(null);
          await supabase.auth.signOut();
          setIsLoading(false);
          return;
        }
      }
      
      // Only set session if it's different from the current one
      if (JSON.stringify(newSession) !== JSON.stringify(session)) {
        setSession(newSession);
      }
      
      isAuthenticatedAfterRefresh = !!newSession;
    } catch (error) {
      console.error("Unexpected error refreshing auth:", error);
      setSession(null);
    } finally {
      setIsLoading(false);
      refreshInProgress.current = false;
      console.log("Auth refresh completed, isAuthenticated =", isAuthenticatedAfterRefresh);
    }
  }, [session, supabase]);

  useEffect(() => {
    // Initialize auth state on component mount
    const initializeAuth = async () => {
      await refreshAuth();
    };
    
    // Set a timeout to ensure we don't get stuck in loading state forever
    if (authTimeoutRef.current) {
      clearTimeout(authTimeoutRef.current);
    }
    
    authTimeoutRef.current = setTimeout(() => {
      if (isLoading) {
        console.log("Auth check timeout - forcing completion");
        setIsLoading(false);
        setSession(null);
      }
    }, 5000); // 5 seconds timeout
    
    // Delay the initial auth check slightly to avoid race conditions with other components
    initializeTimeoutRef.current = setTimeout(() => {
      initializeAuth();
    }, 100);

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log("Auth state change event:", event, "Has session:", !!newSession);
        
        if (event === 'SIGNED_OUT') {
          console.log("SIGNED_OUT event detected - clearing session immediately");
          setSession(null);
          setIsLoading(false);
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          console.log(`${event} event detected - updating session`);
          setSession(newSession);
          setIsLoading(false);
        } else if (event === 'INITIAL_SESSION') {
          console.log(`${event} event detected - refreshing auth state`);
          // Delay slightly to avoid conflicts
          setTimeout(async () => {
            await refreshAuth();
          }, 100);
        }
      }
    );

    return () => {
      console.log("Cleaning up auth subscription");
      subscription.unsubscribe();
      
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
      }
      
      if (initializeTimeoutRef.current) {
        clearTimeout(initializeTimeoutRef.current);
      }
    };
  }, [refreshAuth, supabase.auth]);

  const value = {
    session,
    isLoading,
    isAuthenticated: !!session,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
} 